import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { DocumentModel } from '@data-layer/models/documents';
import { DocumentsRepository } from '@data-layer/repositories';
import { UntypedFormControl } from '@angular/forms';
import { CustomValidator } from '@helpers/custom-validator';
import { environment } from '@env/environment';
import { enumDocumentState, enumDocumentType } from '@data-layer/enums/documents';
import { Observable, ReplaySubject, Subscription, timer } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES, POA_FILE_URL } from '@constants/constants';
import { GenericService } from '@services/generic.service';
import { enumMimeType } from '@enum/enumMimeType';
import { enumFileExtension } from '@enum/enumFileExtension';

@Component({
  selector: 'document-upload-desktop',
  templateUrl: './document-upload-desktop.component.html',
  styleUrls: ['./document-upload-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DocumentUploadDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public uploadedDocument: DocumentModel;
  @Input() public attachmentTypeName: string;
  @Input() public attachmentName: string;
  @Input() public attachmentHint: string;
  @Input() public attachmentHintNote: string;
  @Input() public attachmentManagedTooltip: string;
  @Input() public footerHint: string = 'LABELS.DRAG_N_DROP_FOR_UPLOAD';
  @Input() public footerHintAction: () => void;
  @Input() public poaFileUrl: string = POA_FILE_URL;
  @Input() public id: string | number;
  @Input() public documentFileNameBtnClass: string;
  @Input() public canUploadDocuments: boolean = false;
  @Input() public canRemoveDocuments: boolean = false;
  @Input() public canViewDocuments: boolean = false;
  @Input() public canDownloadDocuments: boolean = false;
  @Input() public isRequired: boolean = false;
  @Input() public isCustomBrowseBtn: boolean = false;
  @Input() public isLoading: boolean = false;
  @Input() public isStretched: boolean = false;
  @Input() public isSimpleStripLayout: boolean;
  @Input() public isShowLabelOverUploadedDate: boolean;
  @Input() public isAttachmentHintHtml: boolean;
  @Input() public isFullErrorHighlight: boolean = false;
  @Input() public isStripeLayout: boolean;
  @Input() public isDividedLayout: boolean;
  @Input() public isSm: boolean;
  @Input() public isHideTag: boolean = false;
  @Input() public isShowOptionalTag: boolean = true;
  @Input() public isShowDeferredRequiredTag: boolean = false;
  @Input() public requiredLaterTooltip: string;
  @Input() public isShowEmptyFileTag: boolean;
  @Input() public isShowNormalAddIcon: boolean;
  @Input() public isShowDownloadFile: boolean = false;
  @Input() public isHidePoaDownload: boolean = false;
  @Input() public isShowFileDate: boolean = false;
  @Input() public isShowLoader: boolean = true;
  @Input() public isShowFooter: boolean = true;
  @Input() public isSidebarManagedMode: boolean = false;
  @Input() public fileMimeIgnoreList: enumMimeType[] = [];
  @Input() public fileExtensionIgnoreList: enumFileExtension[] = [];
  @Input() public forceMobileView: boolean = false;
  @Output() public upload: EventEmitter<File> = new EventEmitter<File>();
  @Output() public remove: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();
  @Output() public preview: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();
  @Output() public stateUpdate: EventEmitter<enumDocumentState> = new EventEmitter<enumDocumentState>();
  @Output() public inputClick: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('link', { static: false }) public linkRef: ElementRef;
  @ViewChild('inputElement', { static: false }) public inputElement: ElementRef;

  public fileControl: UntypedFormControl = new UntypedFormControl(null, [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS.filter((fe) => !this.fileExtensionIgnoreList.includes(fe))),
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
  ]);
  public errors = { maxFileSize: 'FORM_ERROR.MAX_FILE_SIZE', fileExtensionNotAllowed: 'FORM_ERROR.FILE_EXTENSION_NOT_ALLOWED' };
  public allowedFileMimes: string = '';
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;

  public isDragOver: boolean;
  public isTablet$: Observable<boolean>;
  public isUploadingDoc: boolean;
  public isRemovingDoc: boolean;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private checkDocumentStateSubscription: Subscription;

  constructor(private documentsRepository: DocumentsRepository, private genericService: GenericService) {
    this.isTablet$ = this.genericService.isTablet$;
  }

  ngOnInit(): void {
    this.allowedFileMimes = ALLOWED_FILE_MIMES.filter((fm) => !this.fileMimeIgnoreList.includes(fm)).join(',');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      (!this.checkDocumentStateSubscription || this.checkDocumentStateSubscription.closed) &&
      this.uploadedDocument?.state === enumDocumentState.SCAN_PENDING
    ) {
      this.checkDocumentState();
    }

    if (!!changes.uploadedDocument && !!this.uploadedDocument?.id && this.isUploadingDoc) {
      this.isUploadingDoc = false;
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public get isDocumentScanPending(): boolean {
    return this.uploadedDocument?.state === enumDocumentState.SCAN_PENDING;
  }

  public get isDocumentScanFailed(): boolean {
    return this.uploadedDocument?.state === enumDocumentState.SCAN_FAILED;
  }

  public get isDocumentUploaded(): boolean {
    return [enumDocumentState.UPLOADED, enumDocumentState.CREATED].includes(this.uploadedDocument?.state);
  }

  public get isDocumentFailedAfterScan(): boolean {
    return this.uploadedDocument?.state === enumDocumentState.FILE_SIZE_TO_BIG_AFTER_SCAN;
  }

  public get isShowFullErrorHighlight() {
    return (this.isDocumentScanFailed || this.isDocumentFailedAfterScan) && this.isFullErrorHighlight;
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      if (this.isShowLoader) {
        this.isUploadingDoc = true;
      }
      this.uploadFile(files[0]);
    }
  }

  public downloadDocument(event?: Event): void {
    // Keep the click from bubbling to the surrounding <label> (it could trigger the hidden file input).
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.documentsRepository
      .getDownloadLink(this.uploadedDocument.id)
      .pipe(take(1))
      .subscribe((linkData: { downloadUrl: string }) => {
        this.linkRef.nativeElement.href = linkData.downloadUrl;
        this.linkRef.nativeElement.click();
      });
  }

  public removeFile(event: Event): void {
    if (!this.canRemoveDocuments) {
      return;
    }
    this.isRemovingDoc = true;
    event.stopPropagation();
    event.preventDefault();
    this.clearFile();
    this.remove.emit(this.uploadedDocument);
  }

  public viewFile(event: Event): void {
    if (!this.canViewDocuments) {
      return;
    }

    this.preview.emit(this.uploadedDocument);
  }

  // #12642 — when download is the only available action, the mobile layout renders
  // a direct download icon instead of a single-item "..." dropdown.
  public get isDownloadOnlyAction(): boolean {
    return this.canDownloadDocuments && !this.canViewDocuments && !this.canRemoveDocuments;
  }

  public get isUploadSuccessful(): boolean {
    return !!this.fileName && !this.isDocumentScanFailed && !this.isDocumentFailedAfterScan && !this.isDocumentScanPending;
  }

  public get fileName(): string {
    return this.uploadedDocument && this.uploadedDocument.fileName ? this.uploadedDocument.fileName : null;
  }

  public onDragOver(event: DragEvent): void {
    if (!this.canUploadDocuments || !!this.fileName) {
      return;
    }
    this.isDragOver = true;
    event.preventDefault();
    event.stopPropagation();
  }

  public onDrop(event: DragEvent): void {
    if (!this.canUploadDocuments || !!this.fileName) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length) {
      if (this.isShowLoader) {
        this.isUploadingDoc = true;
      }
      this.uploadFile(event.dataTransfer.files[0]);
    }
  }

  public onDragLeave(event: DragEvent): void {
    if (!this.canUploadDocuments || !!this.fileName) {
      return;
    }
    this.isDragOver = false;
    event.preventDefault();
    event.stopPropagation();
  }

  public clickInput(): void {
    this.inputClick.emit();
    this.clearFile();
  }

  public uploadClick() {
    this.inputElement.nativeElement.click();
  }

  // Keep a tap on the "Required later" tooltip from bubbling to the surrounding <label> and opening the file picker.
  public onRequiredLaterTooltipClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }

  private uploadFile(file: File): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();
    if (this.fileControl.valid) {
      this.upload.emit(file);
    } else {
      this.isUploadingDoc = false;
    }
    this.inputElement.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }

  private checkDocumentState(): void {
    const timerVal: number = 1000;

    if (this.checkDocumentStateSubscription) {
      this.checkDocumentStateSubscription.unsubscribe();
    }
    this.checkDocumentStateSubscription = timer(timerVal)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.documentsRepository.getDocumentState(this.uploadedDocument.id, false)),
      )
      .subscribe((state: enumDocumentState) => {
        if (state === enumDocumentState.DELETED) {
          this.isRemovingDoc = false;
        }
        if (state !== enumDocumentState.SCAN_PENDING) {
          this.stateUpdate.emit(state);
          this.isUploadingDoc = false;
        } else {
          this.checkDocumentState();
        }
      });
  }

  public onFooterHintClicked(event: Event): void {
    if (!this.footerHintAction) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.footerHintAction();
  }

  public onFooterHintKeydown(event: KeyboardEvent): void {
    if (!this.footerHintAction) {
      return;
    }
    const isActionKey = event.key === 'Enter' || event.key === ' ';
    if (!isActionKey) {
      return;
    }
    event.preventDefault();
    this.footerHintAction();
  }
}
